import React, { useCallback, useEffect, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, IconButton, InputLabel, List, ListItem, ListItemText, MenuItem, Select, Stack,
    Tooltip, Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import Global from "../../customlib/Global";
import livechatApi from "./livechatApi";
import { PASTEL } from "./livechatTheme";

// DEV-1222 - tanda customer dipakai bersama panel konteks Live Chat (CustomerContextPanel) dan
// panel Customer Profile Lotus WA klasik, supaya tampilan & perilaku edit/riwayat identik.
// Backend resolve customer_id dari nomor HP sendiri (resolveCustomerIdFromBody di lotus-be-express).
export function useCustomerFlagInsight(phone, enabled = true) {
    const [flag, setFlag] = useState(null);
    const [insight, setInsight] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!enabled || !phone) {
            setFlag(null);
            setInsight(null);
            return;
        }
        setIsLoading(true);
        Promise.all([
            livechatApi.getCustomerFlag(phone).then(({ data }) => data?.data || null).catch(() => null),
            livechatApi.getCustomerInsight(phone).then(({ data }) => data?.data || null).catch(() => null),
        ])
            .then(([flagData, insightData]) => {
                setFlag(flagData);
                setInsight(insightData);
            })
            .finally(() => setIsLoading(false));
    }, [enabled, phone]);

    // Dipanggil setelah simpan tanda sukses, supaya badge + section ikut update tanpa reload.
    const refetchFlag = useCallback(() => {
        if (!phone) return Promise.resolve();
        return livechatApi
            .getCustomerFlag(phone)
            .then(({ data }) => setFlag(data?.data || null))
            .catch(() => {});
    }, [phone]);

    return { flag, insight, isLoading, refetchFlag };
}

// Mockup BSR-126 AC-3 - badge menonjol saat customer ditandai "tidak bersedia pesan sendiri".
export function SelfOrderFlagBadge({ flag, sx }) {
    if (flag?.self_order_status !== "tidak_bersedia") return null;
    return (
        <Chip
            size="small"
            icon={<FlagRoundedIcon sx={{ fontSize: 14 }} />}
            label="Tidak Bersedia Self Order"
            sx={{ fontWeight: 600, backgroundColor: PASTEL.dangerSoft, color: PASTEL.danger, ...sx }}
        />
    );
}

// DEV-1227 (AC DEV-1222) — tanda customer (DEV-1224: cara bayar/kesediaan self-order) +
// info customer (DEV-1226: jenis customer/saldo poin EP/riwayat order), digabung 1 section
// supaya Sales lihat semuanya otomatis tanpa perlu tanya manual.
export function CustomerFlagInsightSection({ flag, insight, isLoading, phone, onFlagChanged }) {
    // DEV-1222 AC1/AC2 - role yang tidak berhak (Sales biasa, CS, dst) TETAP bisa lihat tapi
    // tombol edit disembunyikan. Aturan role di Global.canManageCustomerFlag (sama dgn backend).
    const canManageFlag = Global.canManageCustomerFlag();
    const [editOpen, setEditOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);

    const customerTypeLabel =
        insight?.customer_type === "company" ? "Corporate" : insight?.customer_type === "personal" ? "Personal" : "-";
    const paymentTypeLabel = flag?.payment_type === "cash" ? "Cash" : flag?.payment_type === "piutang" ? "Piutang" : "-";

    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Tanda Customer
                </Typography>
                <Stack direction="row" spacing={0.25}>
                    <Tooltip title="Riwayat perubahan">
                        <IconButton size="small" onClick={() => setHistoryOpen(true)}>
                            <HistoryRoundedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    {canManageFlag && (
                        <Tooltip title="Ubah tanda customer">
                            <IconButton size="small" onClick={() => setEditOpen(true)}>
                                <EditRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            {editOpen && (
                <CustomerFlagEditDialog
                    phone={phone}
                    flag={flag}
                    onClose={() => setEditOpen(false)}
                    onSaved={() => {
                        setEditOpen(false);
                        onFlagChanged?.();
                    }}
                />
            )}
            {historyOpen && <CustomerFlagHistoryDialog phone={phone} onClose={() => setHistoryOpen(false)} />}

            {isLoading && (
                <Box textAlign="center" sx={{ py: 2 }}>
                    <CircularProgress size={20} />
                </Box>
            )}

            {!isLoading && !flag && !insight && (
                <Alert
                    severity="info" variant="outlined" icon={<InfoOutlinedIcon fontSize="small" />}
                    sx={{ borderRadius: 2, fontSize: 12, mt: 1, mb: 2 }}
                >
                    Belum ada tanda untuk customer ini.
                </Alert>
            )}

            {!isLoading && (flag || insight) && (
                <Stack spacing={1.25} sx={{ mt: 1, mb: 2 }}>
                    <Row icon={<BusinessCenterRoundedIcon sx={{ fontSize: 16 }} />} label="Jenis Customer" value={customerTypeLabel} />
                    <Row icon={<PaymentsRoundedIcon sx={{ fontSize: 16 }} />} label="Cara Bayar" value={paymentTypeLabel} />
                    <Row
                        label="Kesediaan Pesan Sendiri"
                        value={
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                <Chip
                                    size="small"
                                    label={
                                        flag?.self_order_status === "bersedia"
                                            ? "Bersedia"
                                            : flag?.self_order_status === "tidak_bersedia"
                                            ? "Tidak Bersedia"
                                            : "Belum Ditandai"
                                    }
                                    sx={{
                                        fontWeight: 600,
                                        mt: 0.25,
                                        ...(flag?.self_order_status === "bersedia"
                                            ? { backgroundColor: PASTEL.successSoft, color: PASTEL.success }
                                            : flag?.self_order_status === "tidak_bersedia"
                                            ? { backgroundColor: PASTEL.dangerSoft, color: PASTEL.danger }
                                            : {}),
                                    }}
                                />
                                {flag?.self_order_needs_review && (
                                    <Chip size="small" variant="outlined" label="Perlu ditinjau ulang" sx={{ mt: 0.25 }} />
                                )}
                            </Stack>
                        }
                    />
                    <Row
                        icon={<CardGiftcardRoundedIcon sx={{ fontSize: 16 }} />}
                        label="Saldo Poin EP"
                        value={`${new Intl.NumberFormat("id-ID").format(insight?.ep_point_balance ?? 0)} poin`}
                    />
                    {insight?.order_history && (
                        <Row label="Total Order" value={`${insight.order_history.total_orders ?? 0}x`} />
                    )}
                </Stack>
            )}
        </>
    );
}

// Versi mandiri (fetch sendiri) untuk panel Customer Profile Lotus WA klasik (class component,
// tidak bisa pakai hook langsung).
export function CustomerFlagCard({ phone }) {
    const { flag, insight, isLoading, refetchFlag } = useCustomerFlagInsight(phone);

    return (
        <Box>
            <SelfOrderFlagBadge flag={flag} sx={{ mb: 1 }} />
            <CustomerFlagInsightSection
                flag={flag} insight={insight} isLoading={isLoading}
                phone={phone} onFlagChanged={refetchFlag}
            />
        </Box>
    );
}

// DEV-1222 AC1 - form sederhana: cuma 2 field yang benar-benar didukung backend
// (customer_flag.controller.ts#setFlag cuma terima payment_type/self_order_status).
// "Jenis Customer" SENGAJA tidak diikutkan di sini - itu field `customer.type` yang dipakai
// luas di sistem order/POS lain, bukan bagian tabel "tanda" (DEV-1224).
function CustomerFlagEditDialog({ phone, flag, onClose, onSaved }) {
    const [paymentType, setPaymentType] = useState(flag?.payment_type || "");
    const [selfOrderStatus, setSelfOrderStatus] = useState(flag?.self_order_status || "");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    const handleSave = () => {
        const changes = {};
        if (paymentType) changes.payment_type = paymentType;
        if (selfOrderStatus) changes.self_order_status = selfOrderStatus;

        setIsSaving(true);
        setError(null);
        livechatApi
            .setCustomerFlag(phone, changes)
            .then(({ data }) => {
                if (data?.success === false) {
                    setError(data?.message || "Gagal menyimpan tanda customer");
                    return;
                }
                onSaved?.();
            })
            .catch((err) => {
                setError(err?.response?.data?.message || "Gagal menyimpan tanda customer");
            })
            .finally(() => setIsSaving(false));
    };

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Ubah Tanda Customer</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                )}
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="flag-payment-type-label">Cara Bayar</InputLabel>
                        <Select
                            labelId="flag-payment-type-label" label="Cara Bayar"
                            value={paymentType} onChange={(e) => setPaymentType(e.target.value)}
                        >
                            <MenuItem value="">Belum ditandai</MenuItem>
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="piutang">Piutang</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="flag-self-order-label">Kesediaan Pesan Sendiri</InputLabel>
                        <Select
                            labelId="flag-self-order-label" label="Kesediaan Pesan Sendiri"
                            value={selfOrderStatus} onChange={(e) => setSelfOrderStatus(e.target.value)}
                        >
                            <MenuItem value="">Belum ditandai</MenuItem>
                            <MenuItem value="bersedia">Bersedia</MenuItem>
                            <MenuItem value="tidak_bersedia">Tidak Bersedia</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Batal</Button>
                <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? "Menyimpan..." : "Simpan"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// DEV-1222 AC8 - riwayat siapa mengubah apa & kapan (customer_flag_logs, dibaca via
// customer-flag/history). Boleh dilihat siapa saja (konsisten dgn AC2: Sales biasa tetap
// bisa lihat), cuma tombol EDIT yang digating role.
function CustomerFlagHistoryDialog({ phone, onClose }) {
    const [logs, setLogs] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        livechatApi
            .getCustomerFlagHistory(phone)
            .then(({ data }) => setLogs(Array.isArray(data?.data) ? data.data : []))
            .catch(() => setLogs([]))
            .finally(() => setIsLoading(false));
    }, [phone]);

    const fieldLabel = (field) => (field === "payment_type" ? "Cara Bayar" : field === "self_order_status" ? "Kesediaan Pesan Sendiri" : field);

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Riwayat Perubahan Tanda Customer</DialogTitle>
            <DialogContent>
                {isLoading && (
                    <Box textAlign="center" sx={{ py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
                {!isLoading && logs?.length === 0 && (
                    <Alert severity="info" variant="outlined" icon={<InfoOutlinedIcon fontSize="small" />}>
                        Belum ada riwayat perubahan.
                    </Alert>
                )}
                {!isLoading && logs?.length > 0 && (
                    <List dense>
                        {logs.map((log) => (
                            <ListItem key={log.id || `${log.field_name}-${log.created_at}`} disableGutters divider>
                                <ListItemText
                                    primary={`${fieldLabel(log.field_name)}: ${log.old_value || "-"} → ${log.new_value || "-"}`}
                                    secondary={`${log.changed_by_name || "Tidak diketahui"} · ${Global.formatDateTimeToStrDateTime24(log.created_at)}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Tutup</Button>
            </DialogActions>
        </Dialog>
    );
}

export function Row({ label, value, isLink, icon }) {
    return (
        <Box>
            <Typography
                variant="caption" color="text.secondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
                {icon}
                {label}
            </Typography>
            {isLink ? (
                <Typography
                    variant="body2" component="a" href={value} target="_blank" rel="noreferrer"
                    sx={{ display: "block", wordBreak: "break-all", color: PASTEL.primary }}
                >
                    {value}
                </Typography>
            ) : (
                <Typography variant="body2" fontWeight={500}>{value}</Typography>
            )}
        </Box>
    );
}
